import React, { useEffect, useState, useCallback } from "react";
import { 
  Grid, Card, CardContent, Typography, Box, Select, MenuItem, 
  FormControl, InputLabel, Button, CircularProgress, TextField, 
  Chip, IconButton, Tooltip, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, LinearProgress
} from "@mui/material";
import Chart from "react-apexcharts";
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TempleBuddhistIcon from '@mui/icons-material/TempleBuddhist';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const API_BASE_URL = 'https://api.bharatdarshan.app/api';

const ComprehensiveDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [filter, setFilter] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch comprehensive stats
  const fetchDashboardStats = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      let url = `${API_BASE_URL}/dashboard/stats?filter=${filter}`;
      
      if (filter === 'custom' && customStartDate && customEndDate) {
        url = `${API_BASE_URL}/dashboard/stats?startDate=${customStartDate}&endDate=${customEndDate}`;
      }
      
      const [statsResponse, dailyResponse] = await Promise.all([
        fetch(url),
        fetch(`${API_BASE_URL}/dashboard/daily-stats?days=30`)
      ]);
      
      const statsData = await statsResponse.json();
      const dailyData = await dailyResponse.json();
      
      if (statsData.success) {
        setStats(statsData.data);
        setLastUpdated(new Date());
      } else {
        setError(statsData.message || 'Failed to fetch stats');
      }
      
      if (dailyData.success) {
        setDailyStats(dailyData.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter, customStartDate, customEndDate]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Auto refresh every minute
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 60000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchDashboardStats]);

  // Format currency
  const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)} K`;
    return `₹${num.toFixed(0)}`;
  };

  // Format number
  const formatNumber = (num) => {
    const n = Number(num) || 0;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.round((seconds || 0) / 60);
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return `${hrs}h ${remainingMins}m`;
    }
    return `${mins} mins`;
  };

  // Revenue breakdown chart
  const getRevenueBreakdownChart = () => {
    if (!stats) return { options: {}, series: [] };
    
    const revenues = [
      { name: 'Recharge', value: stats.services?.recharge?.revenue || 0 },
      { name: 'Flights', value: stats.services?.flights?.revenue || 0 },
      { name: 'Hotels', value: stats.services?.hotels?.revenue || 0 },
      { name: 'Yatra', value: stats.services?.yatra?.revenue || 0 },
      { name: 'AI Calls', value: stats.services?.aiCalls?.revenue || 0 },
      { name: 'Astrology', value: (stats.services?.astrology?.chatRevenue || 0) + (stats.services?.astrology?.callRevenue || 0) },
      { name: 'E-commerce', value: stats.services?.ecommerce?.revenue || 0 },
      { name: 'Wallet', value: stats.services?.wallet?.amount || 0 }
    ].filter(r => r.value > 0);

    return {
      options: {
        chart: { type: 'donut', fontFamily: 'inherit' },
        labels: revenues.map(r => r.name),
        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#E91E63', '#673AB7', '#FF5722'],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + '%' },
        plotOptions: { 
          pie: { 
            donut: { 
              size: '60%', 
              labels: { 
                show: true, 
                total: { 
                  show: true, 
                  label: 'Total Revenue', 
                  formatter: () => formatCurrency(stats?.totalRevenue || 0) 
                } 
              } 
            } 
          } 
        },
        responsive: [{ breakpoint: 480, options: { chart: { width: 280 }, legend: { position: 'bottom' } } }]
      },
      series: revenues.map(r => r.value)
    };
  };

  // User growth chart
  const getUserGrowthChart = () => {
    if (!dailyStats?.userGrowth) return { options: {}, series: [] };
    
    return {
      options: {
        chart: { type: 'area', fontFamily: 'inherit', toolbar: { show: false }, height: 300 },
        xaxis: { 
          categories: dailyStats.userGrowth.map(d => {
            const date = new Date(d._id);
            return `${date.getDate()}/${date.getMonth() + 1}`;
          }), 
          labels: { rotate: -45, style: { fontSize: '10px' } } 
        },
        colors: ['#4CAF50'],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        title: { text: 'User Growth (Last 30 Days)', align: 'left', style: { fontSize: '14px', fontWeight: 600 } },
        tooltip: { y: { formatter: (val) => `${val} users` } }
      },
      series: [{ name: 'New Users', data: dailyStats.userGrowth.map(d => d.count) }]
    };
  };

  // Recharge trend chart
  const getRechargeTrendChart = () => {
    if (!dailyStats?.rechargeGrowth) return { options: {}, series: [] };
    
    return {
      options: {
        chart: { type: 'bar', fontFamily: 'inherit', toolbar: { show: false } },
        xaxis: { 
          categories: dailyStats.rechargeGrowth.map(d => {
            const date = new Date(d._id);
            return `${date.getDate()}/${date.getMonth() + 1}`;
          }), 
          labels: { rotate: -45, style: { fontSize: '10px' } } 
        },
        colors: ['#2196F3', '#4CAF50'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        dataLabels: { enabled: false },
        title: { text: 'Recharge Trend (Last 30 Days)', align: 'left', style: { fontSize: '14px', fontWeight: 600 } },
        tooltip: { y: { formatter: (val, { seriesIndex }) => seriesIndex === 0 ? `${val} recharges` : `₹${val}` } }
      },
      series: [
        { name: 'Count', data: dailyStats.rechargeGrowth.map(d => d.count) },
        { name: 'Amount', data: dailyStats.rechargeGrowth.map(d => d.amount || 0) }
      ]
    };
  };

  // Services comparison chart
  const getServicesChart = () => {
    if (!stats) return { options: {}, series: [] };
    
    return {
      options: {
        chart: { type: 'bar', fontFamily: 'inherit', toolbar: { show: false } },
        xaxis: { categories: ['Recharge', 'AI Calls', 'Astrology', 'E-commerce', 'Milan Sathi'] },
        colors: ['#4CAF50'],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        dataLabels: { enabled: true, formatter: (val) => formatNumber(val) },
        title: { text: 'Service Transactions', align: 'left', style: { fontSize: '14px', fontWeight: 600 } }
      },
      series: [{
        name: 'Count',
        data: [
          stats.services?.recharge?.total || 0,
          stats.services?.aiCalls?.total || 0,
          (stats.services?.astrology?.chats || 0) + (stats.services?.astrology?.calls || 0),
          stats.services?.ecommerce?.orders || 0,
          stats.services?.milanSathi?.transactions || 0
        ]
      }]
    };
  };

  // Stat Card Component
  const StatCard = ({ title, value, subValue, icon, color, onClick, extra }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        border: '1px solid #e0e0e0',
        '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 6, borderColor: color } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold" sx={{ color, mb: 0.5 }}>
              {value}
            </Typography>
            {subValue && (
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                {subValue}
              </Typography>
            )}
            {extra && (
              <Box mt={1}>
                {extra}
              </Box>
            )}
          </Box>
          <Box sx={{ backgroundColor: `${color}15`, borderRadius: 2, p: 1.5, ml: 1 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Balance Card with progress
  const BalanceCard = ({ title, value, icon, color, status, threshold = 10000 }) => {
    const numValue = Number(String(value).replace(/[^0-9.-]+/g, '')) || 0;
    const progress = Math.min((numValue / threshold) * 100, 100);
    
    return (
      <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${color}08 0%, ${color}03 100%)`, border: `2px solid ${color}30` }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box flex={1}>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                {title}
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" sx={{ color }}>
                {formatCurrency(numValue)}
              </Typography>
              <Box mt={1.5}>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: `${color}20`,
                    '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 }
                  }} 
                />
              </Box>
              <Box mt={1} display="flex" alignItems="center" gap={0.5}>
                {numValue > 5000 ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 16, color: '#f44336' }} />
                )}
                <Chip 
                  label={numValue > 5000 ? 'Sufficient' : 'Low Balance'} 
                  size="small" 
                  sx={{ 
                    backgroundColor: numValue > 5000 ? '#4CAF5020' : '#f4433620',
                    color: numValue > 5000 ? '#4CAF50' : '#f44336',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }} 
                />
              </Box>
            </Box>
            <Box sx={{ backgroundColor: `${color}15`, borderRadius: '50%', p: 2, ml: 1 }}>
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Summary Table
  const SummaryTable = () => (
    <TableContainer component={Paper} sx={{ mt: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Transactions</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Revenue</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><PhoneAndroidIcon sx={{ color: '#4CAF50' }} /> Recharge</Box></TableCell>
            <TableCell align="right">{stats?.services?.recharge?.total || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.recharge?.revenue || 0)}</TableCell>
            <TableCell align="right">
              <Chip label={`✓ ${stats?.services?.recharge?.success || 0}`} size="small" sx={{ mr: 0.5, bgcolor: '#4CAF5020', color: '#4CAF50' }} />
              <Chip label={`✗ ${stats?.services?.recharge?.failed || 0}`} size="small" sx={{ bgcolor: '#f4433620', color: '#f44336' }} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><FlightIcon sx={{ color: '#2196F3' }} /> Flights</Box></TableCell>
            <TableCell align="right">{stats?.services?.flights?.total || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.flights?.revenue || 0)}</TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><HotelIcon sx={{ color: '#FF9800' }} /> Hotels</Box></TableCell>
            <TableCell align="right">{stats?.services?.hotels?.total || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.hotels?.revenue || 0)}</TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><LocationOnIcon sx={{ color: '#9C27B0' }} /> Yatra</Box></TableCell>
            <TableCell align="right">{stats?.services?.yatra?.total || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.yatra?.revenue || 0)}</TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><SmartToyIcon sx={{ color: '#00BCD4' }} /> AI Calls</Box></TableCell>
            <TableCell align="right">{stats?.services?.aiCalls?.total || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.aiCalls?.revenue || 0)}</TableCell>
            <TableCell align="right">{formatDuration(stats?.services?.aiCalls?.duration || 0)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><SupportAgentIcon sx={{ color: '#673AB7' }} /> Astrology</Box></TableCell>
            <TableCell align="right">{(stats?.services?.astrology?.chats || 0) + (stats?.services?.astrology?.calls || 0)}</TableCell>
            <TableCell align="right">{formatCurrency((stats?.services?.astrology?.chatRevenue || 0) + (stats?.services?.astrology?.callRevenue || 0))}</TableCell>
            <TableCell align="right">
              <Chip label={`💬 ${stats?.services?.astrology?.chats || 0}`} size="small" sx={{ mr: 0.5 }} />
              <Chip label={`📞 ${stats?.services?.astrology?.calls || 0}`} size="small" />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><FavoriteIcon sx={{ color: '#E91E63' }} /> Milan Sathi</Box></TableCell>
            <TableCell align="right">{stats?.services?.milanSathi?.transactions || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.milanSathi?.revenue || 0)}</TableCell>
            <TableCell align="right">{stats?.services?.milanSathi?.users || 0} users</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><ShoppingCartIcon sx={{ color: '#795548' }} /> E-commerce</Box></TableCell>
            <TableCell align="right">{stats?.services?.ecommerce?.orders || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.ecommerce?.revenue || 0)}</TableCell>
            <TableCell align="right">{stats?.services?.ecommerce?.products || 0} products</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><TempleBuddhistIcon sx={{ color: '#FF5722' }} /> Puja</Box></TableCell>
            <TableCell align="right">{stats?.services?.puja?.total || 0}</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><Box display="flex" alignItems="center" gap={1}><AccountBalanceWalletIcon sx={{ color: '#607D8B' }} /> Wallet</Box></TableCell>
            <TableCell align="right">{stats?.services?.wallet?.recharges || 0}</TableCell>
            <TableCell align="right">{formatCurrency(stats?.services?.wallet?.amount || 0)}</TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>TOTAL REVENUE</TableCell>
            <TableCell align="right"></TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#4CAF50', fontSize: '1.1rem' }}>{formatCurrency(stats?.totalRevenue || 0)}</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={2}>
        <CircularProgress size={60} sx={{ color: '#FF5722' }} />
        <Typography variant="h6" color="textSecondary">Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={2}>
        <Typography variant="h6" color="error">Error: {error}</Typography>
        <Button variant="contained" onClick={fetchDashboardStats}>Retry</Button>
      </Box>
    );
  }

  const revenueChart = getRevenueBreakdownChart();
  const userChart = getUserGrowthChart();
  const rechargeChart = getRechargeTrendChart();
  const servicesChart = getServicesChart();

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#333' }}>
            📊 Comprehensive Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Complete overview of all Bharat Darshan services • Last updated: {lastUpdated?.toLocaleString() || 'Never'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Period</InputLabel>
            <Select value={filter} label="Period" onChange={(e) => setFilter(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          
          {filter === 'custom' && (
            <>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <Button variant="contained" size="small" onClick={fetchDashboardStats} sx={{ backgroundColor: '#FF5722' }}>
                Apply
              </Button>
            </>
          )}
          
          <Tooltip title={`Click to refresh`}>
            <IconButton onClick={fetchDashboardStats} color="primary" disabled={refreshing}>
              {refreshing ? <AutorenewIcon sx={{ animation: 'spin 1s linear infinite' }} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          
          <Chip
            label={autoRefresh ? '🔄 Auto: ON' : '⏸️ Auto: OFF'}
            color={autoRefresh ? 'success' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{ cursor: 'pointer', fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* API Balances */}
      <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#333' }}>💰 API Balances</Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <BalanceCard
            title="TBO Balance (Flight/Hotel)"
            value={stats?.balances?.tbo || 0}
            icon={<FlightIcon sx={{ color: '#2196F3', fontSize: 36 }} />}
            color="#2196F3"
            threshold={50000}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <BalanceCard
            title="Pay2New Balance (Recharge)"
            value={stats?.balances?.pay2new || 0}
            icon={<PhoneAndroidIcon sx={{ color: '#4CAF50', fontSize: 36 }} />}
            color="#4CAF50"
            threshold={20000}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={formatNumber(stats?.users?.total || 0)}
            subValue={`+${formatNumber(stats?.users?.new || 0)} new in selected period`}
            icon={<PeopleIcon sx={{ color: '#9C27B0', fontSize: 36 }} />}
            color="#9C27B0"
            extra={
              <Box display="flex" alignItems="center" gap={0.5}>
                <TrendingUpIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  Growing
                </Typography>
              </Box>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats?.totalRevenue || 0)}
            subValue="From all services combined"
            icon={<LocalAtmIcon sx={{ color: '#FF5722', fontSize: 36 }} />}
            color="#FF5722"
          />
        </Grid>
      </Grid>

      {/* Services Stats */}
      <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#333' }}>📈 Services Overview</Typography>
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Recharge"
            value={formatNumber(stats?.services?.recharge?.total || 0)}
            subValue={`✓${stats?.services?.recharge?.success || 0} ✗${stats?.services?.recharge?.failed || 0}`}
            icon={<PhoneAndroidIcon sx={{ color: '#4CAF50', fontSize: 28 }} />}
            color="#4CAF50"
            extra={<Typography variant="caption" color="success.main">{formatCurrency(stats?.services?.recharge?.revenue || 0)}</Typography>}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Flights"
            value={formatNumber(stats?.services?.flights?.total || 0)}
            subValue={formatCurrency(stats?.services?.flights?.revenue || 0)}
            icon={<FlightIcon sx={{ color: '#2196F3', fontSize: 28 }} />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Hotels"
            value={formatNumber(stats?.services?.hotels?.total || 0)}
            subValue={formatCurrency(stats?.services?.hotels?.revenue || 0)}
            icon={<HotelIcon sx={{ color: '#FF9800', fontSize: 28 }} />}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="AI Calls"
            value={formatNumber(stats?.services?.aiCalls?.total || 0)}
            subValue={`${formatDuration(stats?.services?.aiCalls?.duration || 0)}`}
            icon={<SmartToyIcon sx={{ color: '#00BCD4', fontSize: 28 }} />}
            color="#00BCD4"
            extra={<Typography variant="caption" color="info.main">{formatCurrency(stats?.services?.aiCalls?.revenue || 0)}</Typography>}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Milan Sathi"
            value={formatNumber(stats?.services?.milanSathi?.users || 0)}
            subValue={`${stats?.services?.milanSathi?.transactions || 0} transactions`}
            icon={<FavoriteIcon sx={{ color: '#E91E63', fontSize: 28 }} />}
            color="#E91E63"
            extra={<Typography variant="caption" color="secondary.main">{formatCurrency(stats?.services?.milanSathi?.revenue || 0)}</Typography>}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Astrology"
            value={(stats?.services?.astrology?.chats || 0) + (stats?.services?.astrology?.calls || 0)}
            subValue={`💬${stats?.services?.astrology?.chats || 0} 📞${stats?.services?.astrology?.calls || 0}`}
            icon={<SupportAgentIcon sx={{ color: '#673AB7', fontSize: 28 }} />}
            color="#673AB7"
            extra={<Typography variant="caption" sx={{ color: '#673AB7' }}>{formatCurrency((stats?.services?.astrology?.chatRevenue || 0) + (stats?.services?.astrology?.callRevenue || 0))}</Typography>}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="E-commerce"
            value={formatNumber(stats?.services?.ecommerce?.orders || 0)}
            subValue={`${stats?.services?.ecommerce?.products || 0} products`}
            icon={<ShoppingCartIcon sx={{ color: '#795548', fontSize: 28 }} />}
            color="#795548"
            extra={<Typography variant="caption" sx={{ color: '#795548' }}>{formatCurrency(stats?.services?.ecommerce?.revenue || 0)}</Typography>}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Yatra"
            value={formatNumber(stats?.services?.yatra?.total || 0)}
            subValue={formatCurrency(stats?.services?.yatra?.revenue || 0)}
            icon={<LocationOnIcon sx={{ color: '#9C27B0', fontSize: 28 }} />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Puja"
            value={formatNumber(stats?.services?.puja?.total || 0)}
            subValue="Total puja offerings"
            icon={<TempleBuddhistIcon sx={{ color: '#FF5722', fontSize: 28 }} />}
            color="#FF5722"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Wallet"
            value={formatNumber(stats?.services?.wallet?.recharges || 0)}
            subValue={formatCurrency(stats?.services?.wallet?.amount || 0)}
            icon={<AccountBalanceWalletIcon sx={{ color: '#607D8B', fontSize: 28 }} />}
            color="#607D8B"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#333' }}>📉 Analytics & Charts</Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Revenue Breakdown</Typography>
            {revenueChart.series.length > 0 ? (
              <Chart options={revenueChart.options} series={revenueChart.series} type="donut" height={350} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                <Typography color="textSecondary">No revenue data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            {userChart.series.length > 0 ? (
              <Chart options={userChart.options} series={userChart.series} type="area" height={380} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={380}>
                <Typography color="textSecondary">No user growth data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            {rechargeChart.series.length > 0 ? (
              <Chart options={rechargeChart.options} series={rechargeChart.series} type="bar" height={350} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                <Typography color="textSecondary">No recharge data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            {servicesChart.series.length > 0 ? (
              <Chart options={servicesChart.options} series={servicesChart.series} type="bar" height={350} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                <Typography color="textSecondary">No services data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Summary Table */}
      <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#333' }}>📋 Detailed Summary</Typography>
      <SummaryTable />

      {/* Footer */}
      <Box mt={4} pt={2} borderTop="1px solid #e0e0e0" textAlign="center">
        <Typography variant="body2" color="textSecondary">
          Dashboard auto-refreshes every 1 minute when enabled • Data from: {stats?.timestamp ? new Date(stats.timestamp).toLocaleString() : 'N/A'}
        </Typography>
      </Box>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default ComprehensiveDashboard;
